import React, { useCallback, useEffect, useRef, useState } from "react";
import useApi from "@/hooks/useApi";
import { CrmAudienceImporter } from "../Campaigns/CrmAudienceImporter";
import {
  PrimaryBlueButton,
  SecondaryBlueButton,
  MeatballMenu,
  MeatballMenuItem
} from "@velaro/velaro-component-lib";
import IconDeactivateUserPerson from "@/icons/IconDeactivateUserPerson";
import IconEditPencil from "@/icons/IconEditPencil";
import IconHistoryTime from "@/icons/IconHistoryTime";
import IconClose from "@/icons/IconClose";
import VariablePicker from "@/components/VariablePicker";

// ─── Types ────────────────────────────────────────────────────────────────────

type CampaignStatus = "Draft" | "Scheduled" | "Running" | "Paused" | "Completed" | "Cancelled";
type TriggerType = "Manual" | "Scheduled" | "AppointmentReminder" | "ContactTagged";

interface IvrCampaign {
  id: number;
  name: string;
  status: CampaignStatus;
  listId: number | null;
  workflowId: number | null;
  workflowName: string | null;
  fromNumber: string;
  callerIdName: string;
  disclosureText: string;
  requireConsentKeypress: boolean;
  transferNumber: string;
  callWindowStartHour: number;
  callWindowEndHour: number;
  maxCallsPerHour: number;
  retryAttempts: number;
  retryDelayMinutes: number;
  scheduledAt: string | null;
  startedAt: string | null;
  completedAt: string | null;
  totalContacts: number;
  calledCount: number;
  answeredCount: number;
  completedCount: number;
  optedOutCount: number;
  transferredCount: number;
  noAnswerCount: number;
  failedCount: number;
  createdAt: string;
  createdBy: string;
  // New fields
  triggerType?: TriggerType;
  triggerTag?: string | null;
  smsFollowupEnabled?: boolean;
  smsFollowupTemplate?: string | null;
  callWindowTimezone?: string;
  consentKey?: number;
  optOutKey?: number;
}

interface IvrPhoneNumber {
  phoneNumber: string;
  label: string;
  isIvr: boolean;
}

interface IvrCall {
  id: number;
  phone: string;
  contactName: string;
  contactEmail: string;
  status: string;
  callSid: string;
  retryNumber: number;
  calledAt: string | null;
  answeredAt: string | null;
  endedAt: string | null;
  durationSeconds: number;
  dispositionCode: string;
  recordingUrl: string | null;
}

interface ContactList {
  id: number;
  name: string;
  memberCount: number;
}

interface IvrQuota {
  enableOutboundIvr: boolean;
  ivrTermsAcceptedAt: string | null;
  ivrTermsAcceptedBy: string | null;
  maxCallsPerMonth: number;
  currentMonthCallCount: number;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const STATUS_COLORS: Record<string, string> = {
  Draft:     "bg-slate-100 text-slate-600",
  Scheduled: "bg-blue-50 text-blue-700",
  Running:   "bg-green-50 text-green-700",
  Paused:    "bg-amber-50 text-amber-700",
  Completed: "bg-purple-50 text-purple-700",
  Cancelled: "bg-red-50 text-red-700"
};

const CALL_STATUS_COLORS: Record<string, string> = {
  Pending:     "text-slate-500",
  Initiating:  "text-blue-500",
  Ringing:     "text-blue-600",
  InProgress:  "text-green-600",
  NoAnswer:    "text-amber-600",
  Busy:        "text-amber-700",
  Failed:      "text-red-600",
  Completed:   "text-green-700",
  OptedOut:    "text-orange-600",
  Transferred: "text-purple-600"
};

function StatusBadge({ status }: { status: string }) {
  return (
    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${STATUS_COLORS[status] ?? "bg-slate-100 text-slate-600"}`}>
      {status}
    </span>
  );
}

function fmtHour(h: number) {
  if (h === undefined || h === null || isNaN(h)) return "—";
  if (h === 0) return "12 AM";
  if (h < 12) return `${h} AM`;
  if (h === 12) return "12 PM";
  return `${h - 12} PM`;
}

/** Normalise a raw phone input to E.164. 10 digits → +1XXXXXXXXXX, etc. */
function normalizePhone(raw: string): string {
  const digits = raw.replace(/\D/g, "");
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith("1")) return `+${digits}`;
  if (raw.trim().startsWith("+")) return raw.trim();
  if (digits.length > 0) return `+${digits}`;
  return raw.trim();
}

const TCPA_TEMPLATE =
  "This is an automated call from [Your Company Name]. You are receiving this call because [reason]. " +
  "This call may be monitored or recorded. To continue, press 0. To be removed from our call list, press 9.";

const TZ_OPTIONS = [
  { value: "contact", label: "Contact's local timezone (recommended)" },
  { value: "America/New_York",    label: "Eastern (ET)" },
  { value: "America/Chicago",     label: "Central (CT)" },
  { value: "America/Denver",      label: "Mountain (MT)" },
  { value: "America/Los_Angeles", label: "Pacific (PT)" },
  { value: "America/Phoenix",     label: "Arizona (no DST)" },
  { value: "America/Anchorage",   label: "Alaska (AKT)" },
  { value: "Pacific/Honolulu",    label: "Hawaii (HT)" },
  { value: "UTC",                 label: "UTC" },
];

function fmtTz(tz: string | undefined) {
  if (!tz || tz === "contact") return "contact tz";
  const opt = TZ_OPTIONS.find(o => o.value === tz);
  return opt ? opt.label.split(" (")[0] : tz;
}

// ─── TCPA Terms Modal ──────────────────────────────────────────────────────────

function TcpaTermsModal({ onAccept, onClose }: { onAccept(): void; onClose(): void }) {
  const [checked, setChecked] = useState(false);
  const [saving, setSaving] = useState(false);
  const api = useApi();

  async function accept() {
    setSaving(true);
    try {
      await api.admin.post("IvrCampaigns/AcceptTerms", {});
      onAccept();
    } catch {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 flex flex-col max-h-[90vh]">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="text-lg font-semibold text-slate-800">IVR Outbound Calling — TCPA Compliance Terms</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 text-xl leading-none" aria-label="Close">×</button>
        </div>
        <div className="px-6 py-4 overflow-y-auto text-sm text-slate-700 space-y-4 flex-1">
          <p className="font-medium text-slate-900">Before using outbound IVR calling, you must agree to the following:</p>
          <ol className="list-decimal pl-5 space-y-2">
            <li>You have obtained prior express written consent from all contacts you intend to call, or you are calling contacts who have an established business relationship with you, as required by the <strong>Telephone Consumer Protection Act (TCPA)</strong>.</li>
            <li>All outbound calls will include a legally compliant disclosure identifying your company, the purpose of the call, and a contact number where your organization can be reached.</li>
            <li>You will honor all opt-out requests immediately. Contacts who press 9 (or otherwise request removal) will be permanently added to your suppression list and will not be called again.</li>
            <li>You will only place calls during permitted hours: <strong>9 AM – 8 PM local time</strong> in the recipient's timezone, Monday through Friday, unless separate state-specific hour rules are in place.</li>
            <li>You will not use this service for spam, harassment, or any unlawful purpose. Velaro reserves the right to suspend access if calling patterns suggest abuse.</li>
            <li>You accept sole legal responsibility for your calling campaigns and will indemnify Velaro against any TCPA claims arising from your use of this feature.</li>
          </ol>
          <p className="text-xs text-slate-500">This acceptance is logged with your account name, timestamp, and IP address for compliance records. Velaro staff can review acceptance in the activity log.</p>
        </div>
        <div className="px-6 py-4 border-t bg-slate-50 rounded-b-lg">
          <label className="flex items-start gap-3 cursor-pointer mb-4">
            <input type="checkbox" checked={checked} onChange={e => setChecked(e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-slate-300 text-blue-600" />
            <span className="text-sm text-slate-700">
              I have read and agree to the TCPA compliance terms above. I confirm I have proper consent from all contacts and will comply with all applicable calling regulations.
            </span>
          </label>
          <div className="flex justify-end gap-3">
            <SecondaryBlueButton label="Not now" onClick={onClose} />
            <PrimaryBlueButton label={saving ? "Saving…" : "Accept & Enable IVR Calling"} onClick={accept} disabled={!checked || saving} />
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Test Call Modal ───────────────────────────────────────────────────────────

interface TestCallModalProps {
  campaign: IvrCampaign;
  onClose(): void;
}

function TestCallModal({ campaign, onClose }: TestCallModalProps) {
  const api = useApi();
  const [phone, setPhone] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  async function placeCall() {
    const normalized = normalizePhone(phone);
    if (!normalized) { setError("Phone number is required."); return; }
    if (!/^\+\d{7,15}$/.test(normalized)) { setError("Could not parse that number — try including your country code, e.g. +12125551234."); return; }
    setPhone(normalized);
    setSending(true);
    setError("");
    try {
      await api.admin.post(`IvrCampaigns/${campaign.id}/TestCall`, { phone: normalized });
      setSuccess(true);
    } catch (e: unknown) {
      const err = e as { json?: { error?: string } };
      setError(err?.json?.error ?? "Failed to place test call. Check the campaign from-number and try again.");
      setSending(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="text-lg font-semibold text-slate-800">Test Call — {campaign.name}</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 text-xl" aria-label="Close">×</button>
        </div>

        <div className="px-6 py-4 space-y-4">
          {success ? (
            <div className="space-y-4">
              <div className="bg-green-50 border border-green-200 text-green-800 rounded px-4 py-3 text-sm font-medium">
                Call placed to {phone.trim()}! Check your phone.
              </div>
              <div className="flex justify-end">
                <SecondaryBlueButton label="Dismiss" onClick={onClose} />
              </div>
            </div>
          ) : (
            <>
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2 rounded">{error}</div>
              )}

              <label className="block">
                <span className="text-sm text-slate-600 font-medium">Phone Number</span>
                <input
                  className="mt-1 w-full border border-slate-300 rounded px-3 py-2 text-sm font-mono"
                  value={phone}
                  onChange={e => setPhone(e.target.value)}
                  onBlur={e => setPhone(normalizePhone(e.target.value))}
                  placeholder="2125551234 or +12125551234"
                  autoFocus
                />
                <span className="text-xs text-slate-400 mt-1 block">US numbers: just enter 10 digits — we'll add the +1 for you.</span>
              </label>

              {campaign.disclosureText && (
                <div className="space-y-1">
                  <span className="text-sm text-slate-600 font-medium">Disclosure Preview</span>
                  <div className="bg-slate-50 border border-slate-200 rounded px-3 py-2 text-sm text-slate-700 max-h-28 overflow-y-auto">
                    <span className="text-xs text-slate-400 block mb-1 uppercase tracking-wide">What will be spoken:</span>
                    {campaign.disclosureText}
                  </div>
                </div>
              )}

              <div className="flex justify-end gap-3 pt-1">
                <SecondaryBlueButton label="Cancel" onClick={onClose} />
                <PrimaryBlueButton label={sending ? "Calling…" : "Place Test Call"} onClick={placeCall} disabled={sending} />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Campaign Form Modal ───────────────────────────────────────────────────────

interface CampaignFormProps {
  campaign?: IvrCampaign | null;
  lists: ContactList[];
  onSave(): void;
  onClose(): void;
}

function CampaignFormModal({ campaign, lists: initialLists, onSave, onClose }: CampaignFormProps) {
  const api = useApi();
  const [saving, setSaving]         = useState(false);
  const [error, setError]           = useState("");
  const [lists, setLists]           = useState(initialLists);
  const [showImporter, setShowImporter] = useState(false);
  const [phoneNumbers, setPhoneNumbers] = useState<IvrPhoneNumber[]>([]);

  useEffect(() => {
    api.admin.get("IvrCampaigns/PhoneNumbers")
      .then(r => r.json())
      .then(d => setPhoneNumbers(Array.isArray(d) ? d : []))
      .catch(() => {});
  }, []);

  const disclosureRef = useRef<HTMLTextAreaElement>(null);
  const smsRef        = useRef<HTMLTextAreaElement>(null);

  function makeInsertHandler(ref: React.RefObject<HTMLTextAreaElement>, field: "disclosureText" | "smsFollowupTemplate") {
    return function handleInsert(variable: string) {
      const el = ref.current;
      if (el) {
        const start = el.selectionStart ?? (form[field]?.length || 0);
        const end   = el.selectionEnd   ?? start;
        const next  = (form[field] || "").slice(0, start) + variable + (form[field] || "").slice(end);
        set(field, next);
        requestAnimationFrame(() => {
          el.focus();
          const newPos = start + variable.length;
          el.setSelectionRange(newPos, newPos);
        });
      } else {
        set(field, (form[field] || "") + variable);
      }
    };
  }

  const [form, setForm] = useState({
    name:                   campaign?.name ?? "",
    listId:                 campaign?.listId ?? null as number | null,
    fromNumber:             campaign?.fromNumber ?? "",
    callerIdName:           campaign?.callerIdName ?? "",
    disclosureText:         campaign?.disclosureText ?? TCPA_TEMPLATE,
    requireConsentKeypress: campaign?.requireConsentKeypress ?? true,
    consentKey:             campaign?.consentKey ?? 0,
    optOutKey:              campaign?.optOutKey  ?? 9,
    transferNumber:         campaign?.transferNumber ?? "",
    callWindowStartHour:    campaign?.callWindowStartHour ?? 9,
    callWindowEndHour:      campaign?.callWindowEndHour ?? 20,
    callWindowTimezone:     campaign?.callWindowTimezone ?? "contact",
    maxCallsPerHour:        campaign?.maxCallsPerHour ?? 30,
    retryAttempts:          campaign?.retryAttempts ?? 1,
    retryDelayMinutes:      campaign?.retryDelayMinutes ?? 60,
    // Trigger type fields
    triggerType:            (campaign?.triggerType ?? "Manual") as TriggerType,
    scheduledAt:            campaign?.scheduledAt ?? "",
    triggerTag:             campaign?.triggerTag ?? "",
    // SMS follow-up fields
    smsFollowupEnabled:     campaign?.smsFollowupEnabled ?? false,
    smsFollowupTemplate:    campaign?.smsFollowupTemplate ?? "",
  });

  function set(key: string, val: unknown) {
    setForm(f => ({ ...f, [key]: val }));
  }

  async function save() {
    if (!form.name.trim()) { setError("Campaign name is required."); return; }
    if (!form.fromNumber.trim()) { setError("From phone number is required."); return; }
    if (!form.disclosureText.trim()) { setError("Disclosure text is required (TCPA compliance)."); return; }
    setSaving(true); setError("");
    try {
      const body = {
        ...form,
        scheduledAt: form.triggerType === "Scheduled" && form.scheduledAt ? form.scheduledAt : null,
        triggerTag:  form.triggerType === "ContactTagged" ? form.triggerTag : null,
        smsFollowupTemplate: form.smsFollowupEnabled ? form.smsFollowupTemplate : null,
      };
      if (campaign?.id) {
        await api.admin.put(`IvrCampaigns/${campaign.id}`, body);
      } else {
        await api.admin.post("IvrCampaigns", body);
      }
      onSave();
    } catch (e: unknown) {
      const err = e as { json?: { error?: string } };
      setError(err?.json?.error ?? "Failed to save campaign.");
      setSaving(false);
    }
  }

  const hours = Array.from({ length: 24 }, (_, i) => i);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 flex flex-col max-h-[90vh]">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="text-lg font-semibold text-slate-800">
            {campaign ? "Edit IVR Campaign" : "New IVR Campaign"}
          </h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 text-xl" aria-label="Close">×</button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-5">
          {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2 rounded">{error}</div>}

          {/* Basic info */}
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">Campaign Info</h3>
            <label className="block">
              <span className="text-sm text-slate-600">Campaign Name *</span>
              <input className="mt-1 w-full border border-slate-300 rounded px-3 py-2 text-sm"
                value={form.name} onChange={e => set("name", e.target.value)} placeholder="e.g. Spring Outreach 2026" />
            </label>
            <div className="block">
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm text-slate-600">Contact List</span>
                <button type="button" onClick={() => setShowImporter(true)}
                  className="text-xs text-cornflower-blue-600 hover:underline font-medium">
                  + Import from CRM
                </button>
              </div>
              <select className="w-full border border-slate-300 rounded px-3 py-2 text-sm"
                value={form.listId ?? ""} onChange={e => set("listId", e.target.value ? Number(e.target.value) : null)}>
                <option value="">— Select a list —</option>
                {lists.map(l => <option key={l.id} value={l.id}>{l.name} ({l.memberCount ?? 0} contacts)</option>)}
              </select>
            </div>

            {showImporter && (
              <CrmAudienceImporter
                onClose={() => setShowImporter(false)}
                onImported={(listId, listName) => {
                  setLists(prev => [...prev, { id: listId, name: listName, memberCount: 0 } as ContactList]);
                  set("listId", listId);
                  setShowImporter(false);
                }}
              />
            )}

            {/* Trigger Type */}
            <label className="block">
              <span className="text-sm text-slate-600">Trigger Type</span>
              <select className="mt-1 w-full border border-slate-300 rounded px-3 py-2 text-sm"
                value={form.triggerType}
                onChange={e => set("triggerType", e.target.value as TriggerType)}>
                <option value="Manual">Manual — launch this campaign manually</option>
                <option value="Scheduled">Scheduled — run at a specific date/time</option>
                <option value="AppointmentReminder">Appointment Reminder — 24h before booked appointment</option>
                <option value="ContactTagged">Contact Tagged — triggered when a tag is applied</option>
              </select>
            </label>

            {form.triggerType === "Scheduled" && (
              <label className="block">
                <span className="text-sm text-slate-600">Scheduled Date & Time</span>
                <input
                  type="datetime-local"
                  className="mt-1 w-full border border-slate-300 rounded px-3 py-2 text-sm"
                  value={form.scheduledAt}
                  onChange={e => set("scheduledAt", e.target.value)}
                />
              </label>
            )}

            {form.triggerType === "AppointmentReminder" && (
              <div className="bg-blue-50 border border-blue-100 rounded px-3 py-2 text-sm text-blue-800">
                This campaign will automatically call contacts 24 hours before their booked appointment.
              </div>
            )}

            {form.triggerType === "ContactTagged" && (
              <label className="block">
                <span className="text-sm text-slate-600">Trigger Tag Name</span>
                <input
                  className="mt-1 w-full border border-slate-300 rounded px-3 py-2 text-sm"
                  value={form.triggerTag}
                  onChange={e => set("triggerTag", e.target.value)}
                  placeholder="e.g. payment-overdue"
                />
                <span className="text-xs text-slate-400 mt-1 block">
                  When this tag is applied to a contact, they will be added to this campaign and called.
                </span>
              </label>
            )}
          </div>

          {/* Calling config */}
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">Calling Config</h3>
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="text-sm text-slate-600">From Number *</span>
                {phoneNumbers.length > 0 ? (
                  <select className="mt-1 w-full border border-slate-300 rounded px-3 py-2 text-sm"
                    value={form.fromNumber}
                    onChange={e => set("fromNumber", e.target.value)}>
                    <option value="">— Select a number —</option>
                    {phoneNumbers.map(n => (
                      <option key={n.phoneNumber} value={n.phoneNumber}>
                        {n.label !== n.phoneNumber ? `${n.label} — ${n.phoneNumber}` : n.phoneNumber}
                        {n.isIvr ? " (IVR)" : ""}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input className="mt-1 w-full border border-slate-300 rounded px-3 py-2 text-sm"
                    value={form.fromNumber}
                    onChange={e => set("fromNumber", e.target.value)}
                    onBlur={e => set("fromNumber", normalizePhone(e.target.value))}
                    placeholder="2125551234 or +12125551234" />
                )}
              </label>
              <label className="block">
                <span className="text-sm text-slate-600">Caller ID Name</span>
                <input className="mt-1 w-full border border-slate-300 rounded px-3 py-2 text-sm"
                  value={form.callerIdName} onChange={e => set("callerIdName", e.target.value)} placeholder="Acme Corp" />
              </label>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="text-sm text-slate-600">Call Window Start</span>
                <select className="mt-1 w-full border border-slate-300 rounded px-3 py-2 text-sm"
                  value={form.callWindowStartHour} onChange={e => set("callWindowStartHour", Number(e.target.value))}>
                  {hours.map(h => <option key={h} value={h}>{fmtHour(h)}</option>)}
                </select>
              </label>
              <label className="block">
                <span className="text-sm text-slate-600">Call Window End</span>
                <select className="mt-1 w-full border border-slate-300 rounded px-3 py-2 text-sm"
                  value={form.callWindowEndHour} onChange={e => set("callWindowEndHour", Number(e.target.value))}>
                  {hours.map(h => <option key={h} value={h}>{fmtHour(h)}</option>)}
                </select>
              </label>
            </div>
            <label className="block">
              <span className="text-sm text-slate-600">Call Window Timezone</span>
              <select className="mt-1 w-full border border-slate-300 rounded px-3 py-2 text-sm"
                value={form.callWindowTimezone}
                onChange={e => set("callWindowTimezone", e.target.value)}>
                {TZ_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </label>
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="text-sm text-slate-600">Max Calls / Hour</span>
                <input type="number" min={1} max={300} className="mt-1 w-full border border-slate-300 rounded px-3 py-2 text-sm"
                  value={form.maxCallsPerHour} onChange={e => set("maxCallsPerHour", Number(e.target.value))} />
              </label>
              <label className="block">
                <span className="text-sm text-slate-600">Transfer Number</span>
                <input className="mt-1 w-full border border-slate-300 rounded px-3 py-2 text-sm"
                  value={form.transferNumber} onChange={e => set("transferNumber", e.target.value)} placeholder="+18005551234" />
              </label>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="text-sm text-slate-600">Retry Attempts (no-answer)</span>
                <input type="number" min={0} max={5} className="mt-1 w-full border border-slate-300 rounded px-3 py-2 text-sm"
                  value={form.retryAttempts} onChange={e => set("retryAttempts", Number(e.target.value))} />
              </label>
              <label className="block">
                <span className="text-sm text-slate-600">Retry Delay (minutes)</span>
                <input type="number" min={15} max={1440} className="mt-1 w-full border border-slate-300 rounded px-3 py-2 text-sm"
                  value={form.retryDelayMinutes} onChange={e => set("retryDelayMinutes", Number(e.target.value))} />
              </label>
            </div>
          </div>

          {/* TCPA compliance */}
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">Legal / TCPA</h3>
            <p className="text-xs text-slate-500 bg-blue-50 border border-blue-100 rounded p-2">
              TCPA requires you to identify yourself and the purpose of the call at the start of every outbound call.
              This text is spoken verbatim using AI voice when the contact answers.
            </p>
            <div className="block">
              <span className="text-sm text-slate-600">Disclosure Statement (spoken at call start) *</span>
              <div className="relative mt-1">
                <div className="absolute right-1 top-1 z-10">
                  <VariablePicker onInsert={makeInsertHandler(disclosureRef, "disclosureText")} />
                </div>
                <textarea rows={4} ref={disclosureRef}
                  className="w-full border border-slate-300 rounded px-3 py-2 pr-12 text-sm resize-none"
                  value={form.disclosureText}
                  onChange={e => set("disclosureText", e.target.value)}
                  placeholder={TCPA_TEMPLATE} />
              </div>
              <div className="flex items-center justify-between mt-0.5">
                <span className="text-xs text-slate-400">{form.disclosureText.length} characters</span>
                {!form.disclosureText && (
                  <button type="button"
                    className="text-xs text-blue-600 hover:underline"
                    onClick={() => { set("disclosureText", TCPA_TEMPLATE); requestAnimationFrame(() => disclosureRef.current?.focus()); }}>
                    Copy template into field →
                  </button>
                )}
              </div>
            </div>
            <label className="flex items-start gap-3 cursor-pointer">
              <input type="checkbox" checked={form.requireConsentKeypress}
                onChange={e => set("requireConsentKeypress", e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-slate-300 text-blue-600" />
              <span className="text-sm text-slate-700">
                Require consent keypress
                <span className="text-xs text-slate-400 block mt-0.5">Recommended — creates a TCPA consent record for each recipient</span>
              </span>
            </label>
            {form.requireConsentKeypress && (
              <div className="pl-7 grid grid-cols-2 gap-3">
                <label className="block">
                  <span className="text-sm text-slate-600">Continue key (consent)</span>
                  <select className="mt-1 w-full border border-slate-300 rounded px-3 py-2 text-sm"
                    value={form.consentKey}
                    onChange={e => set("consentKey", Number(e.target.value))}>
                    {[0,1,2,3,4,5,6,7,8].map(k => <option key={k} value={k}>Press {k}</option>)}
                  </select>
                </label>
                <label className="block">
                  <span className="text-sm text-slate-600">Opt-out key</span>
                  <select className="mt-1 w-full border border-slate-300 rounded px-3 py-2 text-sm"
                    value={form.optOutKey}
                    onChange={e => set("optOutKey", Number(e.target.value))}>
                    {[1,2,3,4,5,6,7,8,9].map(k => <option key={k} value={k}>Press {k}</option>)}
                  </select>
                </label>
              </div>
            )}
          </div>

          {/* SMS Follow-up */}
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">SMS Follow-up</h3>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={form.smsFollowupEnabled}
                onChange={e => set("smsFollowupEnabled", e.target.checked)}
                className="h-4 w-4 rounded border-slate-300 text-blue-600"
              />
              <span className="text-sm text-slate-700">
                Send SMS follow-up when contact presses 1 (consents)
              </span>
            </label>

            {form.smsFollowupEnabled && (
              <div className="space-y-3 pl-7">
                <div className="block">
                  <span className="text-sm text-slate-600">SMS Message</span>
                  <div className="relative mt-1">
                    <div className="absolute right-1 top-1 z-10">
                      <VariablePicker onInsert={makeInsertHandler(smsRef, "smsFollowupTemplate")} />
                    </div>
                    <textarea
                      ref={smsRef}
                      rows={3}
                      className="w-full border border-slate-300 rounded px-3 py-2 pr-12 text-sm resize-none"
                      value={form.smsFollowupTemplate}
                      onChange={e => set("smsFollowupTemplate", e.target.value)}
                      placeholder="Hi <<name>>, click here to complete your payment: <<paymentLink>>"
                    />
                  </div>
                  <span className="text-xs text-slate-400 mt-0.5 block">
                    Workflow nodes can inject variables like <code className="font-mono">{"<<paymentLink>>"}</code> at call time.
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="px-6 py-4 border-t bg-slate-50 rounded-b-lg flex justify-end gap-3">
          <SecondaryBlueButton label="Cancel" onClick={onClose} />
          <PrimaryBlueButton label={saving ? "Saving…" : "Save Campaign"} onClick={save} disabled={saving} />
        </div>
      </div>
    </div>
  );
}

// ─── Transcript Panel ──────────────────────────────────────────────────────────

function TranscriptPanel({ data, onClose }: { data: Record<string, unknown>; onClose(): void }) {
  const turns = Array.isArray(data?.transcript)
    ? (data.transcript as Array<{ role: string; text: string; ts: string }>)
    : [];

  return (
    <div className="absolute inset-0 bg-white z-10 flex flex-col rounded-lg">
      <div className="flex items-center justify-between px-6 py-4 border-b">
        <div>
          <h3 className="font-semibold text-slate-800">Call Transcript</h3>
          <p className="text-xs text-slate-500">{String(data?.phone ?? "")} — {String(data?.dispositionCode ?? "")}</p>
        </div>
        <button onClick={onClose} className="text-slate-400 hover:text-slate-600 text-xl" aria-label="Back">←</button>
      </div>
      <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3">
        {turns.length === 0 ? (
          <p className="text-slate-400 text-sm">No transcript recorded for this call.</p>
        ) : turns.map((t, i) => (
          <div key={i} className={`flex gap-3 ${t.role === "caller" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-lg rounded-lg px-3 py-2 text-sm ${
              t.role === "caller" ? "bg-blue-600 text-white" :
              t.role === "bot" ? "bg-slate-100 text-slate-800" :
              "bg-amber-50 text-amber-800 text-xs italic"
            }`}>
              <p>{t.text}</p>
              <p className="text-xs opacity-60 mt-1">{new Date(t.ts).toLocaleTimeString()}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Call Log Panel ────────────────────────────────────────────────────────────

function CallLogPanel({ campaign, onClose }: { campaign: IvrCampaign; onClose(): void }) {
  const api = useApi();
  const [calls, setCalls] = useState<IvrCall[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [transcript, setTranscript] = useState<Record<string, unknown> | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = statusFilter ? `?status=${statusFilter}&page=${page}` : `?page=${page}`;
      const res = await api.admin.get(`IvrCampaigns/${campaign.id}/Calls${q}`);
      const data = await res.json();
      setCalls(Array.isArray(data.items) ? data.items : []);
      setTotal(data.total ?? 0);
    } catch { setCalls([]); }
    finally { setLoading(false); }
  }, [campaign.id, page, statusFilter]);

  useEffect(() => { load(); }, [load]);

  async function loadTranscript(callId: number) {
    try {
      const res = await api.admin.get(`IvrCampaigns/${campaign.id}/Calls/${callId}/Transcript`);
      const data = await res.json();
      setTranscript(data as Record<string, unknown>);
    } catch { /* ignore */ }
  }

  const statuses = ["", "Pending", "Initiating", "Ringing", "InProgress", "NoAnswer", "Busy", "Failed", "Completed", "OptedOut", "Transferred"];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl mx-4 flex flex-col max-h-[90vh] relative">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div>
            <h2 className="text-lg font-semibold text-slate-800">Call Log — {campaign.name}</h2>
            <p className="text-sm text-slate-500">{total} total calls</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 text-xl" aria-label="Close">×</button>
        </div>

        {/* Stats bar */}
        <div className="px-6 py-3 bg-slate-50 border-b flex flex-wrap gap-4 text-sm">
          <span><span className="font-semibold text-blue-700">{campaign.calledCount}</span> called</span>
          <span><span className="font-semibold text-green-700">{campaign.answeredCount}</span> answered</span>
          <span><span className="font-semibold text-purple-700">{campaign.completedCount}</span> completed</span>
          <span><span className="font-semibold text-orange-600">{campaign.optedOutCount}</span> opted out</span>
          <span><span className="font-semibold text-amber-600">{campaign.noAnswerCount}</span> no answer</span>
          <span><span className="font-semibold text-slate-600">{campaign.transferredCount}</span> transferred</span>
          <span><span className="font-semibold text-red-600">{campaign.failedCount}</span> failed</span>
        </div>

        <div className="px-6 py-3 border-b flex gap-3 items-center">
          <span className="text-sm text-slate-600">Filter:</span>
          <select className="border border-slate-300 rounded px-2 py-1 text-sm"
            value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }}>
            {statuses.map(s => <option key={s} value={s}>{s || "All statuses"}</option>)}
          </select>
          <SecondaryBlueButton label="Refresh" onClick={() => load()} />
        </div>

        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center h-32 text-slate-400">Loading…</div>
          ) : calls.length === 0 ? (
            <div className="flex items-center justify-center h-32 text-slate-400 text-sm">No calls found.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-4 py-2.5 text-left font-semibold text-slate-600">Phone</th>
                  <th className="px-4 py-2.5 text-left font-semibold text-slate-600">Contact</th>
                  <th className="px-4 py-2.5 text-left font-semibold text-slate-600">Status</th>
                  <th className="px-4 py-2.5 text-left font-semibold text-slate-600">Called At</th>
                  <th className="px-4 py-2.5 text-left font-semibold text-slate-600">Duration</th>
                  <th className="px-4 py-2.5 text-left font-semibold text-slate-600">Attempt</th>
                  <th className="px-4 py-2.5 w-1"></th>
                </tr>
              </thead>
              <tbody>
                {calls.map(call => (
                  <tr key={call.id} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="px-4 py-3 font-mono">{call.phone}</td>
                    <td className="px-4 py-3 text-slate-600">{call.contactName || "—"}</td>
                    <td className="px-4 py-3">
                      <span className={`font-medium ${CALL_STATUS_COLORS[call.status] ?? ""}`}>{call.status}</span>
                    </td>
                    <td className="px-4 py-3 text-slate-500">{call.calledAt ? new Date(call.calledAt).toLocaleString() : "—"}</td>
                    <td className="px-4 py-3 text-slate-500">{call.durationSeconds > 0 ? `${call.durationSeconds}s` : "—"}</td>
                    <td className="px-4 py-3 text-slate-500">{call.retryNumber > 0 ? `Retry ${call.retryNumber}` : "1st"}</td>
                    <td className="px-4 py-3">
                      <button onClick={() => loadTranscript(call.id)} className="text-xs text-blue-600 hover:underline">Transcript</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {total > 50 && (
          <div className="px-6 py-3 border-t flex items-center gap-3 text-sm">
            <button disabled={page === 1} onClick={() => setPage(p => p - 1)}
              className="px-3 py-1 border rounded disabled:opacity-40">Previous</button>
            <span>Page {page} of {Math.ceil(total / 50)}</span>
            <button disabled={page * 50 >= total} onClick={() => setPage(p => p + 1)}
              className="px-3 py-1 border rounded disabled:opacity-40">Next</button>
          </div>
        )}

        {transcript && <TranscriptPanel data={transcript} onClose={() => setTranscript(null)} />}
      </div>
    </div>
  );
}

// ─── Not-enabled screen (shown when IVR is not on the customer's plan) ────────

function IvrNotEnabled() {
  return (
    <div className="flex flex-col items-center justify-center min-h-96 text-center px-8 py-16">
      <div className="text-5xl mb-5">📞</div>
      <h2 className="text-xl font-semibold text-slate-800 mb-3">IVR Outbound Calling</h2>
      <p className="text-slate-500 max-w-sm mb-2">
        Reach your contacts with AI-voiced outbound calls — automated disclosures,
        consent capture, SMS follow-ups, and live agent transfers.
      </p>
      <p className="text-slate-400 text-sm max-w-xs">
        This feature is not included in your current plan.
        Contact <a href="mailto:support@velaro.com" className="text-blue-600 underline">support@velaro.com</a> to add IVR Outbound to your account.
        {/* TODO(billing): Replace mailto link with <UpgradeButton feature="IVR Outbound Campaigns" plan="Add-on" /> → Stripe Checkout. See docs/self-serve-billing-todo.md */}
      </p>
    </div>
  );
}

// ─── Main Page ─────────────────────────────────────────────────────────────────

export default function IvrCampaigns() {
  const api = useApi();
  const [campaigns, setCampaigns] = useState<IvrCampaign[]>([]);
  const [lists, setLists] = useState<ContactList[]>([]);
  const [quota, setQuota] = useState<IvrQuota | null>(null);
  const [loading, setLoading] = useState(true);
  const [showTerms, setShowTerms] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState<IvrCampaign | null>(null);
  const [viewingCallLog, setViewingCallLog] = useState<IvrCampaign | null>(null);
  const [testCallCampaign, setTestCallCampaign] = useState<IvrCampaign | null>(null);
  const [actionError, setActionError] = useState("");
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const load = useCallback(async () => {
    try {
      const [cRes, lRes, qRes] = await Promise.all([
        api.admin.get("IvrCampaigns"),
        api.admin.get("ContactLists"),
        api.admin.get("Campaigns/Quota")
      ]);
      const cData = await cRes.json();
      const lData = await lRes.json();
      const qData = await qRes.json();
      setCampaigns(Array.isArray(cData) ? cData : []);
      setLists(Array.isArray(lData) ? lData : []);
      setQuota(qData ?? null);
    } catch {
      setCampaigns([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    pollRef.current = setInterval(() => {
      setCampaigns(prev => {
        if (prev.some(c => c.status === "Running")) { load(); }
        return prev;
      });
    }, 30000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [load]);

  async function action(id: number, verb: "Launch" | "Pause" | "Cancel") {
    setActionError("");
    try {
      await api.admin.post(`IvrCampaigns/${id}/${verb}`, {});
      await load();
    } catch (e: unknown) {
      const err = e as { json?: { error?: string } };
      setActionError(err?.json?.error ?? `Failed to ${verb.toLowerCase()} campaign.`);
    }
  }

  async function deleteCampaign(id: number) {
    if (!confirm("Delete this campaign? This cannot be undone.")) return;
    try {
      await api.admin.delete(`IvrCampaigns/${id}`);
      await load();
    } catch { setActionError("Failed to delete campaign."); }
  }

  function openNew() {
    if (!quota?.ivrTermsAcceptedAt) { setShowTerms(true); return; }
    setEditingCampaign(null);
    setShowForm(true);
  }

  const ivrEnabled = quota?.enableOutboundIvr ?? false;

  if (!ivrEnabled) {
    return <IvrNotEnabled />;
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-slate-800">IVR Outbound Campaigns</h1>
          {quota?.ivrTermsAcceptedAt && (
            <p className="text-xs text-slate-400 mt-0.5">
              TCPA terms accepted {new Date(quota.ivrTermsAcceptedAt).toLocaleDateString()} by {quota.ivrTermsAcceptedBy}
              {" · "}
              <button onClick={() => setShowTerms(true)} className="underline hover:text-slate-600">Review</button>
            </p>
          )}
        </div>
        <PrimaryBlueButton label="New Campaign" onClick={openNew} />
      </div>

      {/* Quota bar */}
      {quota && (quota.maxCallsPerMonth ?? 0) > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 flex items-center gap-4 text-sm">
          <span className="text-blue-800 font-medium">Monthly calls:</span>
          <span className="text-blue-700">{quota.currentMonthCallCount ?? 0} / {quota.maxCallsPerMonth}</span>
          <div className="flex-1 h-1.5 bg-blue-200 rounded-full">
            <div className="h-full bg-blue-600 rounded-full"
              style={{ width: `${Math.min(100, ((quota.currentMonthCallCount ?? 0) / quota.maxCallsPerMonth) * 100)}%` }} />
          </div>
        </div>
      )}

      {actionError && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-lg flex items-center justify-between">
          <span>{actionError}</span>
          <button onClick={() => setActionError("")} aria-label="Dismiss" className="text-red-500 hover:text-red-700 text-lg leading-none">×</button>
        </div>
      )}

      {/* Campaign table */}
      {loading ? (
        <div className="flex items-center justify-center h-32 text-slate-400">Loading…</div>
      ) : campaigns.length === 0 ? (
        <div className="border border-dashed border-slate-300 rounded-lg flex flex-col items-center justify-center py-16 text-center">
          <div className="text-3xl mb-3">📋</div>
          <p className="text-slate-600 font-medium mb-1">No IVR campaigns yet</p>
          <p className="text-sm text-slate-400 mb-4">Create your first campaign to start making outbound calls.</p>
          <PrimaryBlueButton label="New Campaign" onClick={openNew} />
        </div>
      ) : (
        <div className="max-h-[600px] overflow-y-auto rounded-lg border border-slate-200">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200 sticky top-0">
              <tr>
                <th className="px-4 py-2.5 text-left font-semibold text-slate-600">Campaign</th>
                <th className="px-4 py-2.5 text-left font-semibold text-slate-600">Status</th>
                <th className="px-4 py-2.5 text-left font-semibold text-slate-600">Progress</th>
                <th className="px-4 py-2.5 text-left font-semibold text-slate-600">Call Window</th>
                <th className="px-4 py-2.5 text-left font-semibold text-slate-600">Created</th>
                <th className="px-4 py-2.5 w-1"></th>
              </tr>
            </thead>
            <tbody>
              {campaigns.map(row => {
                const canLaunch = row.status === "Draft" || row.status === "Paused";
                const canPause  = row.status === "Running";

                const meatballItems: MeatballMenuItem[] = [
                  row.status !== "Running"
                    ? { icon: IconEditPencil, label: "Edit", onClick: () => { setEditingCampaign(row); setShowForm(true); } }
                    : null,
                  { icon: IconHistoryTime, label: "Call Log", onClick: () => setViewingCallLog(row) },
                  row.status === "Running"
                    ? { icon: IconClose, label: "Cancel Campaign", onClick: () => action(row.id, "Cancel") }
                    : null,
                  row.status !== "Running"
                    ? { icon: IconDeactivateUserPerson, label: "Delete", onClick: () => deleteCampaign(row.id) }
                    : null,
                ].filter((x): x is MeatballMenuItem => x !== null);

                return (
                  <tr key={row.id} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-800">{row.name}</div>
                      <div className="text-xs text-slate-400 mt-0.5 flex flex-wrap gap-x-2">
                        {row.fromNumber && <span>{row.fromNumber}</span>}
                        {row.workflowName && <span>→ {row.workflowName}</span>}
                      </div>
                    </td>
                    <td className="px-4 py-3"><StatusBadge status={row.status} /></td>
                    <td className="px-4 py-3">
                      {row.totalContacts > 0 ? (
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <div className="h-1.5 w-20 bg-slate-100 rounded-full overflow-hidden">
                              <div className="h-full bg-green-500 rounded-full"
                                style={{ width: `${Math.min(100, (row.calledCount / row.totalContacts) * 100)}%` }} />
                            </div>
                            <span className="text-xs text-slate-500">{row.calledCount}/{row.totalContacts}</span>
                          </div>
                          <div className="text-xs text-slate-400 flex gap-x-2">
                            {row.answeredCount > 0 && <span>{row.answeredCount} ans</span>}
                            {row.optedOutCount > 0 && <span>{row.optedOutCount} opt-out</span>}
                            {row.noAnswerCount > 0 && <span>{row.noAnswerCount} no-ans</span>}
                          </div>
                        </div>
                      ) : <span className="text-xs text-slate-400">Not started</span>}
                    </td>
                    <td className="px-4 py-3 text-slate-600 text-xs">
                      {fmtHour(row.callWindowStartHour)} – {fmtHour(row.callWindowEndHour)}
                      <div className="text-slate-400">{fmtTz(row.callWindowTimezone)} · {row.maxCallsPerHour ?? "—"}/hr</div>
                    </td>
                    <td className="px-4 py-3 text-slate-500 text-xs">{new Date(row.createdAt).toLocaleDateString()}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {canLaunch && <PrimaryBlueButton label="Launch" onClick={() => action(row.id, "Launch")} />}
                        {canPause  && <SecondaryBlueButton label="Pause" onClick={() => action(row.id, "Pause")} />}
                        <SecondaryBlueButton label="Calls" onClick={() => setViewingCallLog(row)} />
                        <SecondaryBlueButton label="Test Call" onClick={() => setTestCallCampaign(row)} />
                        <MeatballMenu menuItems={meatballItems} />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Modals */}
      {showTerms && (
        <TcpaTermsModal
          onAccept={async () => { setShowTerms(false); await load(); setShowForm(true); }}
          onClose={() => setShowTerms(false)} />
      )}
      {showForm && (
        <CampaignFormModal
          campaign={editingCampaign}
          lists={lists}
          onSave={async () => { setShowForm(false); await load(); }}
          onClose={() => setShowForm(false)} />
      )}
      {viewingCallLog && (
        <CallLogPanel campaign={viewingCallLog} onClose={() => setViewingCallLog(null)} />
      )}
      {testCallCampaign && (
        <TestCallModal campaign={testCallCampaign} onClose={() => setTestCallCampaign(null)} />
      )}
    </div>
  );
}
